import { useState } from "react";
import styles from './ContactForm.module.css';
import Turnstile from 'react-turnstile';
import ContactFormData, { DEFAULT_CONTACT_FORM_DATA } from "../../models/ContactFormData";
import { validateContactForm, ValidationResult } from "../../lib/validations/Validations";

function ContactForm() {

    const [formData, setFormData] = useState<ContactFormData>(DEFAULT_CONTACT_FORM_DATA);
    const [curCharCount, setCurCharCount] = useState(0);
    const [errors, setErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState('');
    const [sending, setSending] = useState(false);

    const maxTextLength = 250;
    const TEXT_AREA_NAME = "message";
    const bypassCaptcha =
        process.env.NODE_ENV !== "production" &&
        process.env.REACT_APP_BYPASS_CAPTCHA === "true";

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name === TEXT_AREA_NAME) {
            setCurCharCount(value.length);
        }

        if (name === "phone") {
            setFormData(prev => ({ ...prev, [name]: formatPhone(value) }));
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setSending(true);
        
        setSuccessMessage('');
        setErrors([]);

        if (!bypassCaptcha && !formData.turnstileToken) {
            setErrors(["Please complete the CAPTCHA, or try refreshing the page."]);
            setSending(false);
            return;
        }

        const { success, errors } : ValidationResult = validateContactForm(formData);
        if (!success) {
            setErrors(errors ?? []);
            setSending(false);
            return;
        }

        try {

            const payload = {
                ...formData,
                turnstileToken: bypassCaptcha
                    ? "mock-turnstile-token"
                    : formData.turnstileToken,
            };

            const sendEmailResponse = await fetch(
                '/.netlify/functions/send-emails', 
                {
                    method: "POST",
                    body: JSON.stringify(payload),
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            const result = await sendEmailResponse.json().catch(() => null);

            if (!sendEmailResponse.ok) {
                setErrors(result?.errors ?? ["An unexpected error occurred. Please contact us by phone."]);
                return;
            }

            setFormData(DEFAULT_CONTACT_FORM_DATA);
            setCurCharCount(0);
            setSuccessMessage("Thank you for contacting us, we received your email and we'll get back to you shortly. :)");
        } catch (error) {
            setErrors(["An unexpected error occurred. Please contact us by phone."]);
        } finally {
            setSending(false);
        }
    };

    const formatPhone = (value: string) => {
        const digits = value.replace(/\D/g, '').slice(0, 10);

        if (digits.length < 4) return digits;
        if (digits.length < 7)
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return (
        <section className="container mt-3 mb-5">
            <div className="row justify-content-center">
                <div className="col-10 col-md-8">
                    <div className={`${styles.card}`}>
                        <div className="card-body">

                            <form onSubmit={handleSubmit} aria-label="Contact form" className="contact-form" noValidate>
                                <fieldset className="d-flex flex-column">
                                    <legend className="text-center" style={{ fontWeight: 'bold'}}>Contact Us</legend>

                                    <div className="d-flex flex-column">
                                        <label className="form-label" htmlFor="name">Name<span aria-hidden="true">*</span></label>
                                        <input
                                        className="form-control"
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        aria-required="true"
                                        disabled={sending}/>
                                    </div>

                                    <div className="d-flex flex-column">
                                        <label className="form-label" htmlFor="email">Email<span aria-hidden="true">*</span></label>
                                        <input
                                        className="form-control"
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        aria-required="true"
                                        disabled={sending}/>
                                    </div>

                                    <div className="d-flex flex-column">
                                        <label className="form-label" htmlFor="phone">Phone<span aria-hidden="true">*</span></label>
                                        <input
                                        className="form-control"
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        maxLength={14}
                                        value={formData.phone}
                                        inputMode="numeric"
                                        onChange={handleChange}
                                        required
                                        aria-required="true"
                                        disabled={sending}/>
                                    </div>

                                    {/* <div className="d-flex flex-column">
                                        <label 
                                        className="form-label" 
                                        htmlFor="subject">
                                            Subject<span aria-hidden="true">*</span>
                                        </label>

                                        <input
                                        className={`form-control`}
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        required
                                        aria-required="true"
                                        onChange={handleChange}
                                        disabled={sending}/>

                                        {showSubjectUpdateNotice && <small className="text-success">Subject updated</small>}

                                    </div> */}

                                    <div className="d-flex flex-column">
                                        <label 
                                        className="form-label" 
                                        htmlFor="message">
                                            Message<span aria-hidden="true">*</span>
                                        </label>
                                        
                                        <textarea
                                        className="form-control"
                                        id="message"
                                        name={TEXT_AREA_NAME}
                                        rows={5}
                                        maxLength={250}
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        aria-required="true"
                                        disabled={sending}/>
                                        
                                        <p className="text-end">{`${curCharCount}/${maxTextLength}`}</p>
                                    </div>

                                    <div className="d-flex align-items-center justify-content-center">
                                        <Turnstile
                                        sitekey="0x4AAAAAABjIPuJg7qQ75iGL"
                                        onVerify={(token) =>
                                            setFormData(prev => ({ ...prev, turnstileToken: token }))
                                        }/>
                                    </div>

                                    
                                    {/* Success */}
                                    {successMessage && (
                                        <div className="m-1 text-success">
                                            {successMessage}
                                        </div>
                                    )}
                                    {/* Errors */}
                                    <div className="m-1 text-danger">
                                        <ul
                                        data-testid="validation-errors">
                                            {errors.map((error: string, index) => (
                                                <li key={`${error}-${index}`}>{error}</li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="d-flex align-items-center justify-content-center mt-2">
                                        <button 
                                        type="submit" 
                                        className="btn btn-alo w-50"
                                        disabled={sending}>
                                            {sending ? (
                                                <>
                                                <span 
                                                    className="spinner-border spinner-border-sm me-2" 
                                                    role="status" 
                                                    aria-hidden="true"
                                                ></span>
                                                Sending...
                                                </>
                                            ) : (
                                                'Send Message'
                                            )}
                                        </button>
                                    </div>
                                </fieldset>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ContactForm;